import { useState, useImperativeHandle, forwardRef } from 'react';
import { useProjectCommunications } from '../hooks/useProjectCommunications';
import { deleteCommunication } from '../services/clientCommunicationService';
import { CommunicationGrid } from './CommunicationGrid';
import { CommunicationLightbox } from './CommunicationLightbox';
import type { ClientCommunicationView } from '../types';

export interface ClientCommunicationsSectionRef {
  refetch: () => Promise<void>;
}

interface ClientCommunicationsSectionProps {
  projectId: string;
  onRefetchReady?: (refetch: () => Promise<void>) => void;
}

export const ClientCommunicationsSection = forwardRef<
  ClientCommunicationsSectionRef,
  ClientCommunicationsSectionProps
>(function ClientCommunicationsSection({ projectId, onRefetchReady }, ref) {
  const { communications, isLoading, error, refetch } = useProjectCommunications({
    projectId,
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Expose refetch method via ref
  useImperativeHandle(ref, () => ({
    refetch,
  }));

  // Also notify parent of refetch function
  if (onRefetchReady) {
    onRefetchReady(refetch);
  }

  const selectedCommunication = selectedIndex !== null
    ? communications[selectedIndex]
    : null;

  const handleCommunicationClick = (communication: ClientCommunicationView) => {
    const index = communications.findIndex((c) => c.id === communication.id);
    setSelectedIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedIndex(null);
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < communications.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleDelete = async (communication: ClientCommunicationView) => {
    setIsDeleting(true);
    try {
      await deleteCommunication(communication.id, communication.filePath);
      await refetch();
    } catch (err) {
      console.error('Error deleting communication:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async () => {
    // Refetch to get updated data
    await refetch();
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        Error loading communications: {error}
      </div>
    );
  }

  return (
    <>
      <CommunicationGrid
        communications={communications}
        onCommunicationClick={handleCommunicationClick}
        onDelete={handleDelete}
        showDeleteButton
        isLoading={isLoading}
        emptyMessage="No client communications yet"
      />

      {/* Lightbox */}
      {selectedCommunication && (
        <CommunicationLightbox
          communication={selectedCommunication}
          onClose={handleCloseLightbox}
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={selectedIndex !== null && selectedIndex > 0}
          hasNext={selectedIndex !== null && selectedIndex < communications.length - 1}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          showDeleteButton={!isDeleting}
          showEditButton
        />
      )}
    </>
  );
});
